import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DatePipe } from '@angular/common';

import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { ConfigureUserDialogComponent } from '../configure-user-dialog/configure-user-dialog.component';
import { CoreService } from '../../../../../../services/core/core.service';
import { canonicalServiceId, CLIENT_PRODUCT_SERVICE_IDS } from '../../../../../../shared/service-aliases';

/** Servicios de producto visibles en la tabla */
const PRODUCT_SERVICES = CLIENT_PRODUCT_SERVICE_IDS;

/** Nombres de display para cada servicio */
const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  lelfunko: 'Lelfunko',
  airnode: 'Airnode',
  birdattack: 'BirdAttack',
};

/** Mapa de sufijos de rol a etiquetas legibles */
const ROLE_SUFFIX_LABELS: Record<string, string> = {
  admin: 'Administrador',
  viewer: 'Visor',
  operator: 'Operador',
  editor: 'Editor',
};

export interface ServiceAccessEntry {
  key: string;
  displayName: string;
  roleLabel: string | null;
}

@Component({
  selector: 'app-user-table',
  standalone: false,
  templateUrl: './user-table.component.html',
  styleUrl: './user-table.component.scss',
  providers: [ConfirmationService, MessageService],
})
export class UserTableComponent implements OnInit, OnChanges {
  @Input() listOfUsers!: any[];
  @Input() listOfOrganizations!: any[];
  @Output() dataChanged = new EventEmitter<void>();
  @Output() toastMessage = new EventEmitter<{ severity: string; summary: string; detail: string; life?: number }>();

  finalListOfUsers!: any[];
  cols!: any[];
  items: MenuItem[] | undefined;
  expandedRows = {};

  // Variable para controlar el diálogo de edición
  isEditDialogVisible: boolean = false;

  // Datos del usuario seleccionado
  selectedUser: any = null;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private dialogService: DialogService,
    public coreService: CoreService,
    private datePipe: DatePipe
  ) {}

  get currentOrganizationId(): string | null {
    return this.coreService.resolveCurrentOrganizationId();
  }

  ngOnInit() {
    this.items = [
      {
        label: 'Editar',
        icon: 'pi pi-edit',
      },
      {
        label: 'Deshabilitar',
        icon: 'pi pi-disable',
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-delete',
      },
    ];

    this.cols = [
      { field: 'uid', header: 'uid' },
      { field: 'photoUrl', header: 'photoUrl' },
      { field: 'firstName', header: 'firstName' },
      { field: 'lastName', header: 'lastName' },
      { field: 'email', header: 'email' },
      { field: 'role', header: 'role' },
      { field: 'organization', header: 'organization' },
      { field: 'status', header: 'status' },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Si no hubo cambio en listOfUsers, usar el valor actual para no vaciar la tabla
    const previewListOfUsers: any[] = changes['listOfUsers'] ? (changes['listOfUsers']?.currentValue ?? []) : (this.listOfUsers ?? []);

    const currentOrgId = this.coreService.resolveCurrentOrganizationId();

    this.finalListOfUsers = previewListOfUsers.map((user) => {
      // Encontrar membresía de la organización actual
      let currentMembership: any = null;

      if (user?.membership && currentOrgId) {
        if (Array.isArray(user.membership)) {
          currentMembership = user.membership.find((m: any) => m.organization_id === currentOrgId || m.orgId === currentOrgId);
        } else {
          currentMembership = user.membership[currentOrgId];
        }
      }

      // Resolver Rol para mostrar
      let displayRole = '-';
      if (currentMembership) {
        // Preferir bundle_label si existe
        if (currentMembership.bundle_name) {
          displayRole = currentMembership.bundle_name;
        } else if (currentMembership.bundle_label) {
          displayRole = currentMembership.bundle_label;
        } else if (currentMembership.role) {
          displayRole = currentMembership.role;
        } else if (Array.isArray(currentMembership.roles) && currentMembership.roles.length > 0) {
          displayRole = currentMembership.roles.join(', ');
        }
      }

      // Computar acceso a servicios
      const serviceAccess = this.computeServiceAccess(currentMembership);

      return {
        ...user,
        // Propiedad directa para la tabla
        currentRole: displayRole,
        // Guardamos la membresía actual para facilitar operaciones
        currentMembership: currentMembership,
        hasActiveMembership: this.hasActiveMembership(user),
        serviceAccess,
      };
    });
  }

  /**
   * Computa la lista de servicios accesibles y, si el usuario es "Administrador de Producto",
   * resuelve el rol específico por servicio a partir del array roles[] de la membresía.
   */
  private computeServiceAccess(membership: any): ServiceAccessEntry[] {
    if (!membership) return [];

    // 1. Intentar obtener desde available_services
    let availableServices: string[] = Array.isArray(membership.available_services) ? membership.available_services : [];

    // 2. Fallback: derivar servicios de producto desde roles[] (ej: lelfunko_admin → lelfunko)
    const roles: string[] = Array.isArray(membership.roles) ? membership.roles : [];
    if (availableServices.length === 0 && roles.length > 0) {
      const derivedServices = new Set<string>();
      for (const role of roles) {
        // El rol guardado puede llevar el nombre anterior del producto (`qsense_admin`).
        const svc = canonicalServiceId(String(role).split('_')[0]);
        if (PRODUCT_SERVICES.includes(svc)) {
          derivedServices.add(svc);
        }
      }
      availableServices = Array.from(derivedServices);
    }

    const productServices = availableServices
      .map((s: string) => canonicalServiceId(s))
      .filter((s: string) => PRODUCT_SERVICES.includes(s));

    if (productServices.length === 0) return [];

    const bundleId = membership.bundle_id || '';
    const isProductAdmin = bundleId === 'product_admin';

    return productServices.map((svc: string) => {
      let roleLabel: string | null = null;

      if (isProductAdmin) {
        // Intentar encontrar un rol que coincida con el patrón {service}_{suffix}
        const matchingRole = roles.find((r: string) => r.toLowerCase().startsWith(svc + '_'));
        if (matchingRole) {
          const suffix = matchingRole.toLowerCase().substring(svc.length + 1);
          roleLabel = ROLE_SUFFIX_LABELS[suffix] || suffix;
        } else {
          // Fallback: por definición del bundle product_admin => {service}_admin
          roleLabel = 'Administrador';
        }
      }

      return {
        key: svc,
        displayName: SERVICE_DISPLAY_NAMES[svc] || svc,
        roleLabel,
      };
    });
  }

  private anyMembership(user: any): any[] {
    const membership = user?.membership;
    if (!membership) return [];
    // Soporta tanto objeto-indexado por orgId como arreglo de memberships
    if (Array.isArray(membership)) return membership;
    try {
      return Object.values(membership);
    } catch {
      return [];
    }
  }

  hasActiveMembership(user: any): boolean {
    return this.anyMembership(user).some((m: any) => {
      const s = (m?.status || '').toString().toLowerCase();
      return s === 'active';
    });
  }

  /** Determina si el usuario está en estado "invited" (o "pending") en alguna organización */
  hasInvitedMembership(user: any): boolean {
    return this.anyMembership(user).some((m: any) => {
      const s = (m?.status || '').toString().toLowerCase();
      return s === 'invited' || s === 'pending';
    });
  }

  /** Estado global del usuario priorizando invited > active > inactive */
  getUserOverallStatus(user: any): 'invited' | 'active' | 'inactive' {
    // 0) Chequear estado a nivel raíz si viene desde backend
    try {
      const rootStatus = (user?.status || user?.user_status || '').toString().toLowerCase();
      if (rootStatus === 'invited' || rootStatus === 'pending') return 'invited';
      if (rootStatus === 'active') return 'active';
      if (rootStatus === 'inactive') return 'inactive';
    } catch {}

    // 1) Fallback: si nunca hizo login y tiene membresía activa, es un invitado
    //    Esto cubre usuarios nuevos que aún no tienen app-user o cuyo status se perdió
    try {
      if (!user?.last_login_at && this.hasActiveMembership(user)) {
        return 'invited';
      }
    } catch {}

    // 2) Intentar resolver estado respecto a la organización actual
    try {
      const orgId = this.coreService.getCurrentOrganization?.();
      if (orgId && user?.membership) {
        const m = Array.isArray(user.membership)
          ? user.membership.find((x: any) => (x?.organization_id || x?.organizationId || x?.orgId) === orgId)
          : user.membership[orgId];
        if (m) {
          const s = (m?.status || '').toString().toLowerCase();
          if (s === 'invited' || s === 'pending') return 'invited';
          if (s === 'active') return 'active';
          if (s === 'inactive') return 'inactive';
        }
      }
    } catch {}

    // 3) Fallback global: si tiene invitación en alguna organización, mostrar "Invitado"
    if (this.hasInvitedMembership(user)) return 'invited';
    // 4) Si no, si tiene alguna activa en cualquier organización, mostrar "Habilitado"
    if (this.hasActiveMembership(user)) return 'active';
    // 5) Si no, inactivo
    return 'inactive';
  }

  getUserStatusSeverity(user: any): 'secondary' | 'success' | 'danger' {
    const status = this.getUserOverallStatus(user);
    if (status === 'invited') return 'secondary';
    if (status === 'active') return 'success';
    return 'danger';
  }

  getUserStatusLabel(user: any): string {
    const status = this.getUserOverallStatus(user);
    if (status === 'invited') return 'Invitado';
    if (status === 'active') return 'Habilitado';
    return 'Deshabilitado';
  }

  isPendingStatus(status: string | undefined | null): boolean {
    const s = (status || '').toString().toLowerCase();
    return s === 'pending' || s === 'invited';
  }

  isInactiveStatus(status: string | undefined | null): boolean {
    const s = (status || '').toString().toLowerCase();
    return s === 'inactive';
  }

  openAlertToDisableUser(event: any, orgId: string) {
    this.confirmationService.confirm({
      header: `¿Está seguro de deshabilitar a ${event.first_name} ${event.last_name}?`,
      message: `No podrá ingresar a ${orgId}, hasta que vuelva a ser activado`,
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button-sm',
      acceptButtonStyleClass: 'p-button-outlined p-button-sm',
      accept: async () => {
        const params = {
          user_id: event.user_id,
          organization_id: orgId,
          status: 'inactive',
        };
        try {
          await this.coreService.updateMembership(params);
          this.messageService.add({
            severity: 'success',
            summary: 'Confirmado',
            detail: `${event.first_name} ${event.last_name} ha sido deshabilitado`,
            life: 3000,
          });
          this.dataChanged.emit();
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo deshabilitar al usuario',
            life: 5000,
          });
        }
      },
      reject: () => {
        // Do Nothing
      },
    });
  }

  openAlertToEnableUser(event: any, orgId: string) {
    this.confirmationService.confirm({
      header: `¿Está seguro de habilitar a ${event.first_name} ${event.last_name}?`,
      message: `El acceso a ${orgId} será inmediato, una vez confirmada esta acción`,
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button-sm',
      acceptButtonStyleClass: 'p-button-outlined p-button-sm',
      closeOnEscape: false,
      accept: async () => {
        const params = {
          user_id: event.user_id,
          organization_id: orgId,
          status: 'active',
        };
        try {
          await this.coreService.updateMembership(params);
          this.messageService.add({
            severity: 'success',
            summary: 'Confirmado',
            detail: `${event.first_name} ${event.last_name} ha sido habilitado`,
            life: 3000,
          });
          this.dataChanged.emit();
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo habilitar al usuario',
            life: 5000,
          });
        }
      },
      reject: () => {
        // Do Nothing
      },
    });
  }

  openAlertToDeleteUserPermanently(event: any) {
    this.confirmationService.confirm({
      header: `¿Está seguro de eliminar de forma permanente a ${event.first_name} ${event.last_name}?`,
      message: 'El usuario deberá ser creado nuevamente por un Administrador en caso de ser necesario',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button-sm',
      acceptButtonStyleClass: 'p-button-outlined p-button-sm',
      accept: async () => {
        const params = {
          user_id: event.user_id,
          organization_id: this.coreService.getCurrentOrganization(),
        };
        try {
          await this.coreService.deleteMembership(params);
          this.messageService.add({
            severity: 'success',
            summary: 'Confirmado',
            detail: `${event.first_name} ${event.last_name} ha sido eliminado`,
            life: 3000,
          });
          this.dataChanged.emit();
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo eliminar al usuario',
            life: 5000,
          });
        }
      },
      reject: () => {
        // Do Nothing
      },
    });
  }

  openDialogToEditUser(user: any) {
    const ref = this.dialogService.open(ConfigureUserDialogComponent, {
      header: 'Editar Usuario',
      width: '50vw',
      contentStyle: { overflow: 'visible' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      data: {
        isNew: false,
        user: user,
        organizations: this.listOfOrganizations,
      },
    });

    ref?.onClose.subscribe((data: any) => {
      console.log(`REF EDIT USUARIO: `, data);
      if (data?.status) {
        // Emitir al padre para que muestre el toast (el componente puede destruirse durante la recarga)
        this.toastMessage.emit({
          severity: data.severity,
          summary: data?.summary,
          detail: data?.detail ?? data?.details,
          life: data?.life,
        });
      }
      if (data?.updated === true || data?.status === 'success') {
        this.dataChanged.emit();
      }
    });
  }

  formatDate(dateString: string | null | undefined, type: 'date' | 'time'): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';

      if (type === 'date') {
        return this.datePipe.transform(date, 'dd/MM/yyyy') || '-';
      } else {
        return this.datePipe.transform(date, 'HH:mm:ss') || '-';
      }
    } catch {
      return '-';
    }
  }
}
